import * as Sentry from '@sentry/react';

import { readSyntheticSessionFlag } from './synthetic-session';
import { sanitizeSentryEvent } from './sentry-privacy';

/**
 * Error monitoring for the web SPA (Forma issue #43).
 *
 * Deliberately minimal: unhandled errors, promise rejections, and fixed
 * startup/preview failure diagnostics. No
 * performance tracing, no session replay, no logs — those capture user
 * content (routine data, client names) and burn the free-tier quota, which
 * conflicts with the privacy policy. See `beforeSend` for PII scrubbing.
 *
 * The DSN is a public, embeddable value; it lives in `VITE_SENTRY_DSN`.
 * Sentry only initialises in production builds with a DSN present — dev and
 * test builds never send events, so there is no console noise and local
 * errors do not pollute the production issue stream.
 */

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const enabled =
  import.meta.env.PROD &&
  import.meta.env.VITE_FIREBASE_USE_EMULATORS !== 'true' &&
  Boolean(dsn);

/**
 * `__FORMA_APP_VERSION__` is inlined by Vite's `define`, so it is absent under
 * Vitest and in any consumer that compiles this module without the app build.
 * A missing version must degrade to a tag value, never throw on import.
 */
function readAppVersion(): string {
  return typeof __FORMA_APP_VERSION__ === 'string'
    ? __FORMA_APP_VERSION__
    : 'unknown';
}

let initialized = false;

export function initSentry(startup?: { firebaseConfigured: boolean }): void {
  if (!enabled || initialized) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // `release` is injected at build time by the Sentry Vite plugin.
    initialScope: {
      tags: {
        app_version: readAppVersion(),
        synthetic: readSyntheticSessionFlag(),
      },
    },

    // Strictly error monitoring — no tracing / replay / logs.
    integrations: [],
    tracesSampleRate: 0,

    // Do not attach IP addresses or other default PII to events.
    sendDefaultPii: false,

    beforeSend: sanitizeSentryEvent,
  });
  initialized = true;
  // Explicit static builds support running without a Firebase backend. Other
  // modes must keep reporting missing configuration, including unknown modes.
  if (
    startup?.firebaseConfigured === false &&
    import.meta.env.MODE !== 'static'
  ) {
    reportWebDiagnostic('startup', 'firebase-unconfigured');
  }
}

/**
 * Attaches the half of the build identity that only exists after `vite build`
 * (see `scripts/hosting/release-metadata.mjs`). `release` already carries the
 * point of these is to distinguish two deploys of the *same* commit, which a
 * SHA alone cannot do.
 *
 * Events sent before `build-info.json` resolves carry `app_version` but not
 * these — acceptable, because the diagnostics that need them fire seconds into
 * a session, not during boot.
 */
export function reportBuildInfoTags(buildInfo: {
  deploymentId: string;
  builtAt: string;
  cacheName: string;
  branch: string | null;
}): void {
  if (!enabled) {
    return;
  }

  Sentry.setTags({
    deployment_id: buildInfo.deploymentId,
    built_at: buildInfo.builtAt,
    cache_name: buildInfo.cacheName,
    ...(buildInfo.branch ? { build_branch: buildInfo.branch } : {}),
  });
}

/**
 * Account shape for the current session. Deliberately booleans and enums only —
 * this module never identifies users (see `beforeSend`), and these exist so a
 * Firestore slowness report can be attributed to a guest or a signed-in account
 * without sending anything about who they are.
 */
export function reportSessionDiagnosticsTags(session: {
  isAnonymous: boolean;
  hasOwnedRoutines: boolean | undefined;
  profileSyncStatus: string;
}): void {
  if (!enabled) {
    return;
  }

  Sentry.setTags({
    is_anonymous: session.isAnonymous,
    has_owned_routines:
      session.hasOwnedRoutines === undefined
        ? 'unknown'
        : String(session.hasOwnedRoutines),
    profile_sync: session.profileSyncStatus,
  });
}

/** Only fixed, content-free diagnostics, after the existing consent gate opens. */
export function reportWebDiagnostic(
  operation: 'startup' | 'load' | 'save',
  reason: 'firebase-unconfigured' | 'network' | 'internal' | 'unexpected',
): void {
  if (!initialized) return;
  Sentry.captureMessage('Forma web operation failed', {
    level: 'error',
    tags: {
      diagnostic_event: 'web-operation-failed',
      operation,
      reason,
      app_version: readAppVersion(),
    },
    fingerprint: ['web-operation-failed', operation, reason],
  });
}
